import { useState } from 'react';

interface QuestionCardProps {
  questionNumber: number;
  totalQuestions: number;
  question: string;
  options: string[];
  correctAnswer: number;
  solution: string;
  onAnswer: (isCorrect: boolean) => void;
  isAnswered: boolean;
  selectedAnswer: number | null;
}

export default function QuestionCard({
  questionNumber,
  totalQuestions,
  question,
  options,
  correctAnswer,
  solution,
  onAnswer,
  isAnswered,
  selectedAnswer,
}: QuestionCardProps) {
  const [showSolution, setShowSolution] = useState(false);

  const handleOptionClick = (optionIndex: number) => {
    if (isAnswered) return;
    
    const isCorrect = optionIndex === correctAnswer;
    onAnswer(isCorrect);
    setShowSolution(true);
  };

  const getOptionClass = (optionIndex: number) => {
    if (!isAnswered) {
      return 'bg-white hover:bg-emerald-50 border-2 border-gray-200 hover:border-emerald-400 cursor-pointer';
    }
    
    if (optionIndex === correctAnswer) {
      return 'bg-emerald-100 border-2 border-emerald-500';
    }
    
    if (optionIndex === selectedAnswer && optionIndex !== correctAnswer) {
      return 'bg-red-100 border-2 border-red-500';
    }
    
    return 'bg-gray-50 border-2 border-gray-200 cursor-not-allowed opacity-60';
  };

  const getOptionIcon = (optionIndex: number) => {
    if (!isAnswered) return null;
    
    if (optionIndex === correctAnswer) {
      return <i className="ri-check-line text-emerald-600 text-xl"></i>;
    }
    
    if (optionIndex === selectedAnswer && optionIndex !== correctAnswer) {
      return <i className="ri-close-line text-red-600 text-xl"></i>;
    }
    
    return null;
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Question Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium text-emerald-600">
            Question {questionNumber} of {totalQuestions}
          </span>
          {isAnswered && (
            <span className={`text-sm font-semibold ${selectedAnswer === correctAnswer ? 'text-emerald-600' : 'text-red-600'}`}>
              {selectedAnswer === correctAnswer ? 'Correct!' : 'Incorrect'}
            </span>
          )}
        </div>
        
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <p className="text-lg text-gray-800 leading-relaxed whitespace-pre-wrap">{question}</p>
        </div>
      </div>

      {/* Options */}
      <div className="space-y-3 mb-6">
        {options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleOptionClick(index)}
            disabled={isAnswered}
            className={`w-full text-left p-4 rounded-lg transition-all duration-200 flex items-center justify-between ${getOptionClass(index)}`}
          >
            <div className="flex items-start gap-4 flex-1">
              <span className="font-semibold text-gray-700 min-w-[24px]">
                {String.fromCharCode(65 + index)}.
              </span>
              <span className="text-gray-800 flex-1">{option}</span>
            </div>
            {getOptionIcon(index)}
          </button>
        ))}
      </div>

      {/* Solution Panel */}
      {showSolution && isAnswered && (
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg p-6 border-2 border-emerald-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
              <i className="ri-lightbulb-line text-white"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Step-by-Step Solution</h3>
          </div>
          <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
            {solution}
          </div>
        </div>
      )}
    </div>
  );
}